/**
 * Frekvensbegrænsning for annoncer.
 * Sporer visninger pr. annonce-id i localStorage inden for et rullende vindue,
 * så samme annonce ikke dominerer brugerens session.
 */

const STORAGE_KEY = "mne_ad_freq_v1";
const WINDOW_MS = 30 * 60 * 1000; // 30 min rullende vindue
export const DEFAULT_CAP = 3; // maks visninger pr. annonce pr. vindue

type FreqMap = Record<string, number[]>; // id → timestamps

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function read(): FreqMap {
  if (!isBrowser()) return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as FreqMap;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function write(map: FreqMap): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore quota errors
  }
}

function prune(map: FreqMap, now: number): FreqMap {
  const cutoff = now - WINDOW_MS;
  const out: FreqMap = {};
  for (const [id, ts] of Object.entries(map)) {
    const kept = ts.filter((t) => t >= cutoff);
    if (kept.length) out[id] = kept;
  }
  return out;
}

/** Antal visninger for en annonce inden for det rullende vindue. */
export function getShownCount(id: string): number {
  const now = Date.now();
  const map = prune(read(), now);
  return map[id]?.length ?? 0;
}

/** Registrér en visning. Kaldes typisk fra impression-tracking. */
export function recordImpression(id: string): void {
  const now = Date.now();
  const map = prune(read(), now);
  map[id] = [...(map[id] ?? []), now];
  write(map);
}

/** Returnér ID → shown count for alle sporede annoncer. */
export function getAllShownCounts(): Record<string, number> {
  const now = Date.now();
  const map = prune(read(), now);
  const out: Record<string, number> = {};
  for (const [id, ts] of Object.entries(map)) out[id] = ts.length;
  return out;
}
